import styled, { css } from 'styled-components';

import { hoverCapable } from '../../../../../styles/hover';

// ═══════════════════════════════════════════════════════════════════
// CONTAINER
// ═══════════════════════════════════════════════════════════════════

/**
 * Pantalla completa (2026-09-01): el MISMO contenedor pasa a `position: fixed`
 * cubriendo el viewport, en vez de montar el mapa dentro de un modal.
 *
 * El motivo es que así el mapa NO se desmonta: la instancia de Google Maps, el
 * encuadre, la traza en vivo y el tramo seleccionado siguen donde estaban al
 * entrar y al salir. Un modal lo remontaría — y además la app tiene UNA sola
 * ranura de modal, que el detalle del recorrido ya usa para pausas y cierre de
 * tramo.
 *
 * `z-index` = `fixed` (1030) y no `modal` (1040) a propósito: un modal abierto
 * desde el propio mapa debe seguir saliendo POR ENCIMA.
 *
 * Alto en `dvh` con respaldo en `vh` para el WebView viejo: `dvh` es Chrome
 * 108+, y `100vh` en móvil incluye la barra de direcciones — sin el respaldo,
 * un navegador antiguo se queda sin altura declarada.
 */
const fullscreenMap = css`
  /* 🔴 El doble '&&' NO es cosmético: sube la especificidad a 0,2,0 para
     ganarle a las media queries de arriba. styled-components (stylis) emite
     los @media ANIDADOS al FINAL del CSS del componente, así que con
     especificidad igual gana la media query aunque en el fichero esté antes:
     medido en el navegador, a 768px de ancho el mapa expandido se quedaba en
     350px de alto (la regla de tablet) en vez de ocupar la pantalla. En
     escritorio no se ve, porque ninguna media query aplica — y por eso la
     E2E de Chromium de escritorio lo daba por bueno. */
  && {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
    box-shadow: none;
    z-index: ${({ theme }) => theme.zIndex.fixed};
  }
`;

export const MapContainer = styled.div<{ $fullscreen?: boolean }>`
  position: relative;
  width: 100%;
  height: 40rem;
  border-radius: 1.2rem;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.greyLight || '#f5f5f5'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    height: 35rem;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    height: 30rem;
    border-radius: 0.8rem;
  }

  /* En pantalla completa el alto lo manda el viewport, no el breakpoint. El
     bloque gana por especificidad, no por orden — ver el comentario de
     fullscreenMap arriba. Ojo: nada de acentos graves dentro de un comentario
     CSS aquí, que cierran el template literal de styled-components. */
  ${({ $fullscreen }) => $fullscreen && fullscreenMap}
`;

// ═══════════════════════════════════════════════════════════════════
// MAP WRAPPER
// ═══════════════════════════════════════════════════════════════════

export const MapWrapper = styled.div`
  width: 100%;
  height: 100%;

  /* Zoom del mapa, no de la app (feedback usuario 2026-07-24): al hacer pinch
     sobre el mapa, el navegador NO debe hacer zoom de VIEWPORT (agrandar toda
     la app) — que competía con el pinch de Google Maps y a veces ganaba. Con
     'pan-x pan-y' (sin 'pinch-zoom') el navegador permite scroll de la página
     con un dedo (comportamiento 'cooperative' intacto → sigue el aviso "usa
     dos dedos") pero desactiva SU pinch-zoom en el área del mapa; el pinch de
     dos dedos lo maneja el JS de Google Maps (touch-action no afecta a los
     listeners). Acotado al mapa → NO desactiva el zoom del navegador en el
     resto de la app (accesibilidad intacta), a diferencia de user-scalable=no
     global. La intersección de touch-action con los divs internos de Google
     garantiza que ningún descendiente reactive el pinch-zoom de viewport. */
  touch-action: pan-x pan-y;
`;

// ═══════════════════════════════════════════════════════════════════
// LEGEND
// ═══════════════════════════════════════════════════════════════════

export const LegendContainer = styled.div`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing.m};
  left: ${({ theme }) => theme.spacing.m};
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.m};
  border-radius: 0.8rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  z-index: 10;

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    padding: ${({ theme }) => theme.spacing.xs}
      ${({ theme }) => theme.spacing.s};
    bottom: ${({ theme }) => theme.spacing.s};
    left: ${({ theme }) => theme.spacing.s};
  }
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

// ═══════════════════════════════════════════════════════════════════
// LEGEND — collapsed toggle (minimizada por defecto, UX 2026-07-07)
// ═══════════════════════════════════════════════════════════════════

export const LegendToggleButton = styled.button`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing.m};
  left: ${({ theme }) => theme.spacing.m};
  width: 3.6rem;
  height: 3.6rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: box-shadow 0.15s ease;

  /* Guard táctil (2026-09-01, ver .claude/rules/hover-tactil.md): sin él el
     resaltado se queda PEGADO tras el toque en tablet y en el APK, porque el
     navegador no tiene ningún evento con el que quitarlo. Puesto aquí, en el
     botón base, lo heredan también RecenterButton y FullscreenButton. */
  ${hoverCapable} {
    &:hover {
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
    }
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    bottom: ${({ theme }) => theme.spacing.s};
    left: ${({ theme }) => theme.spacing.s};
    width: 3.2rem;
    height: 3.2rem;
  }
`;

// Botón de recentrar (2026-07-08) — mismo botón redondo blanco que el
// toggle de leyenda, apilado ENCIMA de él en la misma esquina (offset =
// alto del toggle 3.6rem + 0.8rem de aire; en tablet 3.2rem + 0.8rem).
// Hereda todo el estilo de LegendToggleButton, solo cambia la posición.
export const RecenterButton = styled(LegendToggleButton)`
  bottom: calc(${({ theme }) => theme.spacing.m} + 4.4rem);

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    bottom: calc(${({ theme }) => theme.spacing.s} + 4rem);
  }
`;

// Botón de pantalla completa (2026-09-01) — tercer piso de la misma pila:
// leyenda (abajo) → recentrar → pantalla completa. El offset es el doble del
// de RecenterButton, con el mismo aire de 0.8rem entre botones.
export const FullscreenButton = styled(LegendToggleButton)`
  bottom: calc(${({ theme }) => theme.spacing.m} + 8.8rem);

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    bottom: calc(${({ theme }) => theme.spacing.s} + 8rem);
  }
`;

/* Wrapper del icono (SVG inline con `stroke="currentColor"`) — hereda el
   color PRIMARY del tema (ROIS azul #686890, CORA granate), no un hex
   hardcodeado. Se inlinea el SVG (no `<img>`) porque una imagen no hereda
   color y la máscara CSS falla con las URLs de assets de Vite. */
export const LegendToggleIcon = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.primary};

  svg {
    width: 1.8rem;
    height: 1.8rem;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    svg {
      width: 1.6rem;
      height: 1.6rem;
    }
  }
`;

export const LegendHeader = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const LegendCloseButton = styled.button`
  border: none;
  background: transparent;
  padding: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

// `display:flex` + centrado — no-op visual para los usos existentes (círculo
// liso, sin hijos); habilita el swatch "Cambio de sentido" (2026-07-09, ver
// `LegendDirectionChangeGlyph` abajo) para centrar el glifo ↺ dentro del
// mismo círculo, en vez de un `LegendColor` aparte por variante.
export const LegendColor = styled.div<{ $color: string }>`
  width: 1.2rem;
  height: 1.2rem;
  background: ${({ $color }) => $color};
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.white};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LegendLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.XS};
  color: ${({ theme }) => theme.colors.black};
`;

// Glifo ↺ dentro del swatch "Cambio de sentido" de la leyenda (2026-07-09,
// fusión de marcadores — ver comentario de sección en MapWithSegments/
// index.tsx): SVG inline con `stroke="currentColor"` (mismo mecanismo que
// LayersIcon/LocateIcon), coloreado blanco para contrastar sobre el swatch
// neutro — mismo criterio de contraste que el glifo real dentro del
// marcador fusionado del mapa (`createDirectionChangeMarkerContent` en
// `components/display/Map/_internal.tsx`, `content.style.color =
// strokeColor`).
export const LegendDirectionChangeGlyph = styled.span`
  display: flex;
  width: 70%;
  height: 70%;
  color: ${({ theme }) => theme.colors.white};

  svg {
    width: 100%;
    height: 100%;
  }
`;

// Sub-cabecera dentro de la leyenda expandida (bloque "Pausas") — mismo
// tamaño de fuente que `LegendLabel`, en negrita, con un pelín de aire
// respecto al bloque anterior (estado de segmento) para leerse como sección
// propia sin necesitar un divisor visual nuevo.
export const LegendSectionTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.XS};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.black};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

// ═══════════════════════════════════════════════════════════════════
// NO MAP MESSAGE (fallback)
// ═══════════════════════════════════════════════════════════════════

export const NoMapMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: ${({ theme }) => theme.spacing.m};
  background: ${({ theme }) => theme.colors.greyLight || '#f5f5f5'};
  color: ${({ theme }) => theme.colors.greyDark};

  span {
    font-size: 4rem;
  }

  p {
    font-size: ${({ theme }) => theme.fontSize.M};
    margin: 0;
    text-align: center;
    max-width: 80%;
  }

  small {
    font-size: ${({ theme }) => theme.fontSize.XS};
    color: ${({ theme }) => theme.colors.greyDark};
  }
`;
